
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../redux/constants/apiConstants';
import {
  DROPDOWN_TYPES,
  DROPDOWN_DEFAULTS,
  DROPDOWN_CONFIG,
} from '../../redux/constants/dropdownConstants';


export const fetchDropdown = createAsyncThunk(
  'dropdowns/fetchDropdown',
  async (
    {
      type = DROPDOWN_TYPES.TASK_MOVEMENT_TRANSPORT,
      pageNumber = DROPDOWN_DEFAULTS.PAGE_NUMBER,
      pageSize = DROPDOWN_DEFAULTS.PAGE_SIZE,
      searchTerm = '',
    },
    { rejectWithValue }
  ) => {
    try {
      if (!Object.values(DROPDOWN_TYPES).includes(type)) {
        throw new Error(`Invalid dropdown type: ${type}`);
      }

      const validPageNumber = Math.max(pageNumber, DROPDOWN_DEFAULTS.PAGE_NUMBER);
      const validPageSize = Math.min(pageSize, DROPDOWN_DEFAULTS.MAX_PAGE_SIZE);

      const response = await axios.get(`${API_URL}/KeyValue/GetDropdown`, {
        params: {
          type,
          pageNumber: validPageNumber,
          pageSize: validPageSize,
          searchTerm: searchTerm.trim(),
        },
      });

      // ========== FIX: Handle both response formats ==========
      const dataObj = response.data.dataObj;

      // Check if response is paginated (has 'data' property) or simple array
      const isPaginated = dataObj && dataObj.data && Array.isArray(dataObj.data);

      if (isPaginated) {
        // PAGINATED RESPONSE (Customer, Product)
        const formatted = (dataObj.data || []).map((item) => ({
          id: item.key,
          label: item.value,
        }));

        return {
          type,
          items: formatted,
          totalRecords: dataObj.totalRecords,
          pageNumber: dataObj.pageNumber,
          pageSize: dataObj.pageSize,
          totalPages: dataObj.totalPages,
          isPaginated: true,
        };
      } else if (Array.isArray(dataObj)) {
        // SIMPLE ARRAY RESPONSE (TaskMovementTransport)
        const formatted = (dataObj || []).map((item) => ({
          id: item.key,
          label: item.value,
        }));

        return {
          type,
          items: formatted.sort((a, b) => a.label.localeCompare(b.label)),
          isPaginated: false,
        };
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Dropdown fetch error:', error);
      return rejectWithValue(error.response?.data?.errMsg || error.message);
    }
  }
);

const initialDropdownState = {
  items: [],
  loading: false,
  error: null,
  pageNumber: DROPDOWN_DEFAULTS.PAGE_NUMBER,
  totalPages: 0,
  totalRecords: 0,
  isPaginated: false,
};

const dropdownSlice = createSlice({
  name: 'dropdowns',
  initialState: {
    [DROPDOWN_TYPES.TASK_MOVEMENT_TRANSPORT]: initialDropdownState,
    [DROPDOWN_TYPES.CUSTOMER]: initialDropdownState,
    [DROPDOWN_TYPES.PRODUCT]: initialDropdownState,
  },
  reducers: {
    clearError: (state, action) => {
      const { dropdownType } = action.payload;
      if (state[dropdownType]) {
        state[dropdownType].error = null;
      }
    },
    resetDropdown: (state, action) => {
      const { dropdownType } = action.payload;
      if (state[dropdownType]) {
        state[dropdownType] = { ...initialDropdownState };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdown.pending, (state, action) => {
        const { type } = action.meta.arg;
        if (state[type]) {
          state[type].loading = true;
          state[type].error = null;
        }
      })
      .addCase(fetchDropdown.fulfilled, (state, action) => {
        const { type, items, pageNumber, totalPages, totalRecords, isPaginated } =
          action.payload;

        if (state[type]) {
          state[type].loading = false;
          state[type].error = null;  // ← Add this

          if (isPaginated) {
            // PAGINATED: Append items if not first page
            if (pageNumber === DROPDOWN_DEFAULTS.PAGE_NUMBER) {
              state[type].items = items;
            } else {
              state[type].items = [...state[type].items, ...items];
            }
            state[type].pageNumber = pageNumber;
            state[type].totalPages = totalPages;
            state[type].totalRecords = totalRecords;
          } else {
            // SIMPLE: Just set items
            state[type].items = items;
            state[type].isPaginated = false;
          }
        }
      })
      .addCase(fetchDropdown.rejected, (state, action) => {
        const { type } = action.meta.arg;
        if (state[type]) {
          state[type].loading = false;
          state[type].error = action.payload;
          console.error(`Error loading ${type}:`, action.payload);
        }
      });
  },
});

export const { clearError, resetDropdown } = dropdownSlice.actions;
export default dropdownSlice.reducer;